import {cn, getNextGroupValue} from '@tempmee/core'
import {OutlineCheckIcon} from '@tempmee/react-icons'
import {
  type HTMLAttributes,
  type ReactNode,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

// ---------------------------------------------------------------------------
// CheckboxGroup context
// ---------------------------------------------------------------------------

interface CheckboxGroupContextValue {
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  error?: boolean
}

const CheckboxGroupContext = createContext<CheckboxGroupContextValue | null>(null)

function useCheckboxGroupContext() {
  return useContext(CheckboxGroupContext)
}

// ---------------------------------------------------------------------------
// CheckboxGroup (Molecule)
// ---------------------------------------------------------------------------

export interface CheckboxGroupProps extends Omit<HTMLAttributes<HTMLFieldSetElement>, 'onChange'> {
  /** Array of currently checked values. */
  value: string[]
  /** Callback when selection changes. */
  onChange: (value: string[]) => void
  /** Whether all checkboxes in the group are disabled. */
  disabled?: boolean
  /** Whether all checkboxes in the group show error state. */
  error?: boolean
  /** Layout orientation. */
  orientation?: 'horizontal' | 'vertical'
  /**
   * Bold heading rendered above the checkboxes, as the fieldset's `<legend>` — so the
   * group takes it as its accessible name. Matches `RadioGroup`'s `label`.
   */
  label?: ReactNode
  children: ReactNode
}

export const CheckboxGroup = forwardRef<HTMLFieldSetElement, CheckboxGroupProps>(
  (
    {
      value,
      onChange,
      disabled = false,
      error = false,
      orientation = 'vertical',
      label,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const ctx = useMemo<CheckboxGroupContextValue>(
      () => ({value, onChange, disabled, error}),
      [value, onChange, disabled, error],
    )

    return (
      <CheckboxGroupContext.Provider value={ctx}>
        <fieldset
          ref={ref}
          className={cn(
            'flex border-0 p-0 m-0 min-w-0',
            orientation === 'vertical' ? 'flex-col gap-2' : 'flex-row gap-4',
            className,
          )}
          {...props}
        >
          {/* A fieldset's first legend sits outside its flex flow, so `gap` doesn't apply
              to it — `mb-1` sets the same 4px heading gap RadioGroup has. */}
          {label && (
            <legend className="mb-1 block p-0 text-base font-bold text-text-primary">
              {label}
            </legend>
          )}
          {children}
        </fieldset>
      </CheckboxGroupContext.Provider>
    )
  },
)
CheckboxGroup.displayName = 'CheckboxGroup'

// ---------------------------------------------------------------------------
// Checkbox (Atom)
// ---------------------------------------------------------------------------

type CheckboxColor = 'primary' | 'secondary'

function getIndicatorClasses(
  isFilled: boolean,
  isError: boolean,
  isDisabled: boolean,
  color: CheckboxColor,
  indicatorSize: string,
): string {
  // Figma's "Secondary" checkbox fills with secondary.main (#0D1F21).
  const fill = color === 'secondary' ? 'bg-secondary border-secondary' : 'bg-primary border-primary'
  // The unchecked (empty) box uses a neutral slate border (#24575D == text-secondary)
  // for BOTH color variants — matching Figma/MUI, where the color only appears once
  // the box is filled. Do NOT tint the outline with the variant color.
  const outline = 'border-text-secondary bg-transparent'
  // Disabled outranks error and color: Figma draws the disabled box as a solid
  // text-disabled (#88B6BC) outline/fill at full opacity, so the box carries the
  // colour itself rather than inheriting a wrapper-level opacity.
  const colorClasses = isDisabled
    ? isFilled
      ? 'bg-text-disabled border-text-disabled'
      : 'border-text-disabled bg-transparent'
    : isError
      ? isFilled
        ? 'bg-error border-error'
        : 'border-error bg-transparent'
      : isFilled
        ? fill
        : outline
  return cn(
    // `relative` keeps the box painting above the ripple, which is an absolutely
    // positioned `::before` on the wrapper and would otherwise cover the fill.
    'relative flex items-center justify-center shrink-0 rounded transition-colors border-2 border-solid',
    indicatorSize,
    colorClasses,
  )
}

/**
 * The hover / focus / pressed ripple — a circle 7px beyond the box on every side
 * (Figma `81:12872`: a 38px round `focusRipple` behind the 24px medium box, and
 * 34/42px behind the 20/28px small/large boxes).
 *
 * It lives on a `::before` of a box-sized wrapper rather than on a larger flex
 * item, so it overflows the control instead of widening it — the label gap and
 * the checkbox's footprint stay the same whether or not the pointer is over it,
 * exactly as the Figma frame overflows its own 32px bounds.
 *
 * Tones come from the shared `action/_states/*` tokens, the same pair {@link Radio}
 * uses, so the two controls respond identically. Figma draws the checkbox's hover
 * and focus ripples with one identical layer, which reads as a duplicated layer
 * rather than a deliberate hover value: it is absent from 9 of the 12 hover
 * variants and byte-identical to focus in the 3 that have it.
 */
function getRippleClasses(isDisabled: boolean): string {
  return cn(
    'relative inline-flex shrink-0 rounded',
    'before:absolute before:-inset-[7px] before:rounded-full before:transition-colors',
    'peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-1',
    !isDisabled && [
      'group-hover:before:bg-action-hover',
      'group-active:before:bg-action-focus',
      'peer-focus-visible:before:bg-action-focus',
    ],
  )
}

/** Box / check-icon / indeterminate-dash / label sizing per checkbox size. */
const CHECKBOX_BOX = {sm: 'size-5', md: 'size-6', lg: 'size-7'} as const
const CHECKBOX_ICON = {sm: 'size-3.5', md: 'size-4', lg: 'size-[18px]'} as const
const CHECKBOX_DASH = {sm: 'h-0.5 w-2', md: 'h-0.5 w-2.5', lg: 'h-0.5 w-3'} as const
const CHECKBOX_LABEL_TEXT = {sm: 'text-sm', md: 'text-base', lg: 'text-base'} as const

function renderCheckboxLabel(
  label: ReactNode,
  size: 'sm' | 'md' | 'lg',
  isDisabled: boolean,
  isChecked: boolean,
): ReactNode {
  if (!label) return null
  return (
    <div className={cn('flex items-center gap-1.5', isDisabled && 'opacity-50')}>
      {typeof label === 'string' ? (
        <span
          className={cn(
            'text-text-primary',
            isChecked ? 'font-bold' : 'font-normal',
            CHECKBOX_LABEL_TEXT[size],
          )}
        >
          {label}
        </span>
      ) : (
        label
      )}
    </div>
  )
}

export interface CheckboxProps extends Omit<HTMLAttributes<HTMLLabelElement>, 'onChange'> {
  /** Value for this checkbox (used by CheckboxGroup). */
  value?: string
  /** Whether this checkbox is checked (standalone mode). */
  checked?: boolean
  /** Renders the indeterminate (mixed) state — a dash instead of a check. Ignored when `checked`. */
  indeterminate?: boolean
  /** Callback when toggled (standalone mode). */
  onChange?: (checked: boolean) => void
  /** Whether this checkbox is disabled. */
  disabled?: boolean
  /** Whether this checkbox shows error state. */
  error?: boolean
  /** Color of the filled/border state. @default 'primary' */
  color?: CheckboxColor
  /** Size of the checkbox indicator. @default 'md' */
  size?: 'sm' | 'md' | 'lg'
  /**
   * Content rendered beside the checkbox. A string label turns bold while checked;
   * a ReactNode label is rendered as-is and styles itself.
   */
  label?: ReactNode
}

export const Checkbox = forwardRef<HTMLLabelElement, CheckboxProps>(
  (
    {
      value,
      checked,
      indeterminate,
      onChange,
      disabled,
      error,
      color = 'primary',
      size = 'md',
      label,
      className,
      ...props
    },
    ref,
  ) => {
    const group = useCheckboxGroupContext()

    const isChecked = group && value ? group.value.includes(value) : (checked ?? false)
    const isIndeterminate = !isChecked && (indeterminate ?? false)
    const isFilled = isChecked || isIndeterminate
    const isDisabled = (group?.disabled || disabled) ?? false
    const isError = (group?.error || error) ?? false

    const handleChange = () => {
      if (isDisabled) return
      if (group && value) {
        group.onChange(getNextGroupValue(group.value, value))
      } else {
        onChange?.(!isChecked)
      }
    }

    const indicatorSize = CHECKBOX_BOX[size]
    const iconSize = CHECKBOX_ICON[size]

    // `aria-checked="mixed"` alone contradicts a native checkbox, whose real
    // state is the DOM `indeterminate` property (React has no prop for it). With
    // only the attribute set, assistive tech and axe see an unchecked box
    // claiming to be mixed (`aria-conditional-attr`). Mirror the two.
    const inputRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
      if (inputRef.current) inputRef.current.indeterminate = isIndeterminate
    }, [isIndeterminate])

    return (
      <label
        ref={ref}
        className={cn(
          // `align-middle`: an `inline-flex` box takes its baseline from its first
          // flex item, and the indicator's baseline depends on whether it holds a
          // check icon — so a default `vertical-align: baseline` made the whole
          // control drop ~2px the moment it was ticked, anywhere it sits in a
          // baseline context (a table cell, a line of text). Aligning to the
          // middle makes the position independent of the checked state.
          'group inline-flex items-center gap-2 align-middle cursor-pointer select-none',
          isDisabled && 'cursor-not-allowed',
          className,
        )}
        {...props}
      >
        <input
          ref={inputRef}
          type="checkbox"
          checked={isChecked}
          aria-checked={isIndeterminate ? 'mixed' : isChecked}
          onChange={handleChange}
          disabled={isDisabled}
          value={value}
          className="sr-only peer"
        />
        <span className={getRippleClasses(isDisabled)}>
          <div className={getIndicatorClasses(isFilled, isError, isDisabled, color, indicatorSize)}>
            {isChecked && <OutlineCheckIcon className={cn(iconSize, 'text-shades-0')} />}
            {isIndeterminate && (
              <span className={cn(CHECKBOX_DASH[size], 'rounded-full bg-shades-0')} />
            )}
          </div>
        </span>
        {renderCheckboxLabel(label, size, isDisabled, isChecked)}
      </label>
    )
  },
)
Checkbox.displayName = 'Checkbox'
